import { PortableText } from '@portabletext/react';
import { Body, H1, H2, H3, PrimaryButton, SubBody, SubTitle2, Tag } from '@rikstv/shared-components';
import { useEffect, useRef } from 'react';
import { trackInternetSalesView } from '~/analytics/eventTracker';
import { captureUXEvent } from '~/analytics/productAnalytics';
import { Card, Icon, Layout, Loader } from '~/components/shared';
import { FlexBox } from '~/components/shared/layout/FlexBox';
import { SupportFooter } from '~/components/shared/support-footer/SupportFooter';
import { typedKeys } from '~/utils/types';
import classes from './InternetLandingPage.module.css';
import { AddressSearchInput } from './components/AddressSearchInput';
import { DetailsSummary, FaqItem } from './components/FaqItem';
import { GET_STARTED_POINTS, SELLINGPOINTS_DATA } from './data';
import { useAddressSearch } from './hooks/useAddressSearch';
import { useLazySanityData } from './hooks/useLazySanityData';
import { ReactComponent as NgtLogoSvg } from './icons/ngt-logo.svg';
import { useInternetFlowStore } from './useInternetStore';

export const Route_InternetLandingPage = 'internett';

const INPUT_ID = 's-input';

let doReset = true;
const resetStoreStateOnceOnLoad = () => {
  if (doReset) {
    doReset = false;
    useInternetFlowStore.getState().resetState();
  }
};

export const InternetLandingPage = () => {
  resetStoreStateOnceOnLoad();
  useEffect(() => trackInternetSalesView(), []);
  const { data, isLoading, setSearchString, selectedAddress, onSelectAutocompleteSuggestion, onClickCheckAvailability, errorMessage } =
    useAddressSearch();
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const {
    displayData: { title, description, image, faqItems },
  } = useLazySanityData();
  useEffect(() => {
    // hide and show search results panel
    if (searchResultsRef.current) {
      if (data) {
        searchResultsRef.current.style.display = 'block';
      } else {
        searchResultsRef.current.style.display = 'none';
        // try-focus on the input
        (document.getElementById(`${INPUT_ID}`) as HTMLInputElement | null)?.focus();
      }
    }
  }, [data]);

  const FAQ = {
    Generelt: faqItems.filter(f => f.category === 'Generelt').map(item => ({ summary: item.question, details: item.answer, section: item.category }) ),
    Internett: faqItems.filter(f => f.category === 'Internett').map(item => ({ summary: item.question, details: item.answer, section: item.category }) ),
    TV: faqItems.filter(f => f.category === 'TV').map(item => ({ summary: item.question, details: item.answer, section: item.category }) ),
  }

  return (
    <>
      <Layout wide className={classes.container}>
        <div>
          {/* HEADER AND SEARCH */}
          <FlexBox className={classes.desktopGrid} direction="column">
            <FlexBox direction="column" gap="calc(16px + var(--rds-fluid-spacing--0-16))">
              <Tag style={{ color: 'var(--red)'}}>- UNDERHOLDNING VIA INTERNETT</Tag>
              <H1>{title}</H1>
              <Body as="div" className={classes.portableText}>
                <PortableText value={description} />
              </Body>
            </FlexBox>
            <Card noMargin color="purple-10">
              <section>
                <NgtLogoSvg height={25} style={{ display: 'block', marginBottom: 'calc(16px + var(--rds-fluid-spacing--0-16))'}} />
                <H2 lookLike='sub-title-2' style={{marginBottom: 'calc(16px + var(--rds-fluid-spacing--0-16))'}} >Sjekk tilgjengelighet der du bor</H2>
                <div className={classes.searchWrapper}>
                  <AddressSearchInput
                    id={INPUT_ID}
                    errorMessage={errorMessage}
                    defaultValue={selectedAddress?.description ?? ''}
                    onChange={(e) => {
                      setSearchString(e.target.value);
                    }}
                  />
                  <div id="search-results" ref={searchResultsRef} className={classes.searchResults}>
                    {isLoading && (
                      <div style={{ paddingBlock: '1rem' }}>
                        <Loader />
                      </div>
                    )}
                    {!isLoading && data && (
                      <ul onKeyDown={handleArrowKeyNavigationInPopupList} data-testid="address-search-results">
                        {data.installationAddresses.length > 0 &&
                          data.installationAddresses.map((addr) => (
                            <li key={addr.description}>
                              <button onClick={() => onSelectAutocompleteSuggestion(addr)}>{addr.description}</button>
                            </li>
                          ))}
                        {data.installationAddresses.length === 0 && <li style={{ padding: '.5rem 1rem' }}>Ingen resultater...</li>}
                      </ul>
                    )}
                  </div>
                </div>
                <br />
                <PrimaryButton
                  isLoading={isLoading}
                  data-testid="check-availability-button"
                  onClick={onClickCheckAvailability}
                  className={classes.availabilityButton}
                >
                  Sjekk tilgjengelighet
                  <Icon
                    type="search"
                    color="white"
                    size={1.5}
                    style={{ fill: 'white' }}
                  />
                </PrimaryButton>
              </section>
            </Card>
          </FlexBox>
          {/* VALUE PROPOSITIONS */}
          <div className={classes.salesPointsPanel}>
            <ul>
              {/* TODO: Move to Sanity */}
              {SELLINGPOINTS_DATA.map(({ icon: Icon, title, text }) => (
              <li key={title}>
                <FlexBox wrap={false} alignItems='center'>
                    <Icon />
                    <FlexBox direction='column' gap='4px'>
                        <H3>{title}</H3>
                        <SubBody>{text}</SubBody>
                    </FlexBox>
                </FlexBox>
              </li>
              ))}
            </ul>
          </div>
          {/* HOW IT WORKS */}
          <Card color='purple-10' className={classes.howItWorksContainer}>
            <div className={classes.howItWorksContent}>
              <div className={classes.imageContainer}>
                <img src={`${image}?auto=format&w=1200`} alt="" />
              </div>
              <FlexBox direction='column' gap='32px'>

              <H2>Slik fungerer det - på 1,2,3</H2>
              <ol className={classes.stepList}>
                {GET_STARTED_POINTS.map(({ title, description }) => (
                  <li key={title}>
                    <DetailsSummary summary={<H3>{title}</H3>} details={<Body>{description}</Body>} />
                </li>))}
              </ol>
              <ScrollTopButton testid="scroll-top-button" />
              </FlexBox>
            </div>
          </Card>
          {/* FAQ */}
          <section data-testid="internett-faq" className={classes.faqSection}>
            <H2>Ofte stilte spørsmål</H2>
            {typedKeys(FAQ).map((section) => {
              if (!FAQ[section].length) {
                return null;
              }
              return (
              <Card key={section} className={classes.faqCard}>
                <SubTitle2>{section}</SubTitle2>
                <section>
                  {FAQ[section].map(item => (<FaqItem key={item.summary} {...item} />))}
                </section>
              </Card>
            )})}
          </section>
        </div>
        <FlexBox direction="column" gap={"calc(16px + var(--rds-fluid-spacing--0-8))"} alignItems="center" style={{ marginTop: '44px' }}>
          <H2>Klar for å bytte internett?</H2>
          <ScrollTopButton testid="bottom-scroll-top-button" />
        </FlexBox>
      </Layout>
      {/* Override phone for specific help around internet */}
      <SupportFooter phoneNumber='210 10 212' />
    </>
  );
};

const ScrollTopButton: React.FC<{testid: string}> = ({ testid }) => (
  <PrimaryButton
    data-testid={testid}
    onClick={() => {
      captureUXEvent('rtv_sales_click_scroll_to_top', { context: testid });
      document.querySelector('h1')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }}
    className={classes.availabilityButton}
  >
    Sjekk tilgjengelighet
    <Icon
      type="search"
      color="white"
      size={1.5}
      style={{ fill: 'white' }}
    />
  </PrimaryButton>
)

const handleArrowKeyNavigationInPopupList: React.KeyboardEventHandler = (e) => {
  // only handle vertical arrow keys
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  // opt out when meta keys are pressed
  if (!(e.target instanceof HTMLElement) || e.altKey || e.shiftKey || e.ctrlKey || e.metaKey) return;
  // opt out when active element is not inside of the popover
  if (!e.target.contains(document.activeElement)) return;

  const liElement = document.activeElement?.parentElement;
  const nextElement = (e.key === 'ArrowDown' ? liElement?.nextElementSibling : liElement?.previousElementSibling)?.querySelector('button');
  if (nextElement) nextElement.focus();
};
